import React, { ReactElement, useEffect, useCallback } from 'react';
import styled from 'styled-components/macro';
import { BoardContext, useBoardStateSelector } from 'store/contexts/board';
import { useContextSelector } from 'use-context-selector';
import { generateEmptyGrid, generateEmptyIndexes } from './utils';
import Grid from './Grid';

import { useBlockStateSelector } from 'store/contexts/block';

interface Props {
  useSelectedSeedOnInit?: boolean;
  numbered?: boolean;
}

const Board = ({
  useSelectedSeedOnInit = false,
  numbered = false,
}: Props): ReactElement => {
  const blockState = useBlockStateSelector();

  const boardState = useBoardStateSelector();
  const setBoardState = useContextSelector(
    BoardContext,
    context => context.setState,
  );
  const numOfRows = boardState.numOfRows;
  const numOfCols = boardState.numOfCols;

  useEffect(() => {
    if (!useSelectedSeedOnInit) {
      setBoardState(state => ({
        ...state,
        grid: generateEmptyGrid(numOfRows, numOfCols),
        hoverIndexes: generateEmptyIndexes(numOfRows),
      }));
    }
  }, [numOfCols, numOfRows, setBoardState, useSelectedSeedOnInit]);

  // Different deps with above, don't combine
  useEffect(() => {
    if (useSelectedSeedOnInit) {
      setBoardState(state => ({
        ...state,
        grid:
          blockState.selectedBlockIndex !== null
            ? blockState.data[blockState.selectedBlockIndex]?.shape
            : generateEmptyGrid(numOfRows, numOfCols),
      }));
    }
  }, [
    blockState.data,
    blockState.selectedBlockIndex,
    numOfCols,
    numOfRows,
    setBoardState,
    useSelectedSeedOnInit,
  ]);

  const onCellAction = useCallback(
    (actionIsClick: boolean, rowIndex?: number, colIndex?: number): void => {
      if (!boardState.isEditingGrid) return;

      const actionResult = actionIsClick
        ? [...boardState.grid]
        : generateEmptyIndexes(boardState.numOfRows);

      if (rowIndex === undefined || colIndex === undefined) {
        actionIsClick
          ? setBoardState(state => ({
              ...state,
              grid: actionResult as boolean[][],
            }))
          : setBoardState(state => ({
              ...state,
              hoverIndexes: actionResult as number[][],
            }));
        return;
      }

      if (!boardState.isPlacingSeed || useSelectedSeedOnInit) {
        actionIsClick
          ? (actionResult[rowIndex][colIndex] = !boardState.grid[rowIndex][
              colIndex
            ])
          : actionResult[rowIndex].push(colIndex as never);
      } else {
        const selectedBlockIndex = blockState.selectedBlockIndex;
        const selectedSeed =
          selectedBlockIndex !== null
            ? blockState.data[selectedBlockIndex]
            : null;

        const centerIndex = [
          Math.floor((selectedSeed?.shape.length || boardState.numOfRows) / 2),
          Math.floor(
            (selectedSeed?.shape[0].length || boardState.numOfCols) / 2,
          ),
        ];

        selectedSeed?.shape.forEach((row, rowId) =>
          row.forEach((cell, cellId) => {
            const rowPosition =
              (rowIndex + rowId + boardState.numOfRows - centerIndex[0]) %
              boardState.numOfRows;
            const colPosition =
              (colIndex + cellId + boardState.numOfCols - centerIndex[1]) %
              boardState.numOfCols;

            actionIsClick
              ? (actionResult[rowPosition][colPosition] =
                  cell || boardState.grid[rowPosition][colPosition])
              : cell && actionResult[rowPosition].push(colPosition as never);
          }),
        );
      }

      actionIsClick
        ? setBoardState(state => ({
            ...state,
            grid: actionResult as boolean[][],
          }))
        : setBoardState(state => ({
            ...state,
            hoverIndexes: actionResult as number[][],
          }));
    },
    [
      blockState.data,
      blockState.selectedBlockIndex,
      boardState.grid,
      boardState.isEditingGrid,
      boardState.isPlacingSeed,
      boardState.numOfCols,
      boardState.numOfRows,
      setBoardState,
      useSelectedSeedOnInit,
    ],
  );

  return (
    <GridContainer onMouseLeave={(): void => onCellAction(false)}>
      <Grid
        grid={boardState.grid}
        hoverIndexes={boardState.hoverIndexes}
        onCellAction={onCellAction}
        numbered={numbered}
      />
    </GridContainer>
  );
};

const GridContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 1.5em;
`;

export default Board;
